
import { networkService } from "@/services/network.service";
import { productRepository } from "@/repositories/product.repository";
import { getUserProducts } from "@/services/products.service";
import { UserProduct } from "@/types";
import OfflineLogger from "@/utils/offlineLogger";

export class ProductSyncService {
  private issyncing = false;
  private lastServerSyncTimestamp = 0;

  async loadLocalProducts(): Promise<UserProduct[]> {
    return productRepository.getAll();
  }

  async syncFromServer(): Promise<UserProduct[]> {
    if (!networkService.isOnline()) {
      OfflineLogger.info('ProductSyncService.syncFromServer', 'Offline mode, loading local products');
      return this.loadLocalProducts();
    }

    if (this.issyncing) {
      OfflineLogger.info('ProductSyncService.syncFromServer', 'Sync already in progress, skipping');
      return this.loadLocalProducts();
    }

    try {
      this.issyncing = true;
      OfflineLogger.info('ProductSyncService.syncFromServer', 'Starting intelligent server sync');

      const serverProducts = await getUserProducts();
      const syncTimestamp = Date.now();

      if (serverProducts.length === 0) {
        const localProducts = await this.loadLocalProducts();
        OfflineLogger.info('ProductSyncService.syncFromServer', 'No server products, returning local only');
        return localProducts;
      }

      // ✅ NUOVO: Sync intelligente solo se i dati del server sono più recenti
      const shouldFullSync = syncTimestamp - this.lastServerSyncTimestamp > 300000; // 5 minuti
      
      if (shouldFullSync) {
        await this.performFullServerSync(serverProducts);
        this.lastServerSyncTimestamp = syncTimestamp;
      }

      // ✅ MIGLIORATO: Combina dati server con pending locali in modo intelligente
      const localProducts = await this.loadLocalProducts();
      const combinedProducts = this.mergeServerAndLocalProducts(serverProducts, localProducts);
      
      OfflineLogger.info('ProductSyncService.syncFromServer', `Intelligent sync completed: ${combinedProducts.length} total products`);
      
      return combinedProducts;
    } catch (error) {
      OfflineLogger.error('ProductSyncService.syncFromServer', error);
      return this.loadLocalProducts();
    } finally {
      this.issyncing = false;
    }
  }

  // ✅ NUOVO: Sync completo dal server solo quando necessario
  private async performFullServerSync(serverProducts: UserProduct[]): Promise<void> {
    const localProducts = await this.loadLocalProducts();
    const localProductIds = new Set(localProducts.map(p => p.id));
    const serverProductIds = new Set(serverProducts.map(p => p.id));
    
    // Trova prodotti nuovi dal server
    const newProducts = serverProducts.filter(p => !localProductIds.has(p.id));
    
    // ✅ NUOVO: Trova prodotti eliminati dal server
    const deletedOnServer = localProducts.filter(p => 
      !this.isOfflineCreatedProduct(p.id) && !serverProductIds.has(p.id)
    );
    
    // Aggiungi nuovi prodotti
    for (const product of newProducts) {
      try {
        await productRepository.create(product);
      } catch (error) {
        OfflineLogger.error('ProductSyncService.performFullServerSync', `Error adding new product ${product.id}: ${error}`);
      }
    }
    
    // ✅ NUOVO: Elimina prodotti che non esistono più sul server
    for (const product of deletedOnServer) {
      try {
        await productRepository.physicalDelete(product.id);
        OfflineLogger.info('ProductSyncService.performFullServerSync', `Deleted product removed from server: ${product.id}`);
        
        // Emetti evento per notificare l'eliminazione
        window.dispatchEvent(new CustomEvent('serverProductDeleted', {
          detail: { productId: product.id }
        }));
      } catch (error) {
        OfflineLogger.error('ProductSyncService.performFullServerSync', `Error deleting server-removed product ${product.id}: ${error}`);
      }
    }
    
    OfflineLogger.info('ProductSyncService.performFullServerSync', `Full server sync: +${newProducts.length} new, -${deletedOnServer.length} deleted`);
  }

  // ✅ NUOVO: Merge intelligente di prodotti server e locali
  private mergeServerAndLocalProducts(serverProducts: UserProduct[], localProducts: UserProduct[]): UserProduct[] {
    const serverProductIds = new Set(serverProducts.map(p => p.id));
    const merged = [...serverProducts];
    
    // Aggiungi solo prodotti locali pending che non sono già nel server
    const pendingLocalProducts = localProducts.filter(p => 
      this.isOfflineCreatedProduct(p.id) || 
      (!serverProductIds.has(p.id) && !p.id.startsWith('deleted-'))
    );
    
    merged.push(...pendingLocalProducts);
    
    // ✅ NUOVO: Rimuovi duplicati finali
    return this.removeFinalDuplicates(merged);
  }

  // ✅ NUOVO: Rimozione duplicati finale più precisa
  private removeFinalDuplicates(products: UserProduct[]): UserProduct[] {
    const seen = new Map<string, UserProduct>();
    
    for (const product of products) {
      const key = `${product.name}-${product.barcode || ''}-${product.categoryId || ''}`;
      const existing = seen.get(key);
      
      if (!existing) {
        seen.set(key, product);
      } else {
        // Preferisci prodotti non-offline su quelli offline
        const isCurrentOffline = this.isOfflineCreatedProduct(product.id);
        const isExistingOffline = this.isOfflineCreatedProduct(existing.id);
        
        if (isExistingOffline && !isCurrentOffline) {
          seen.set(key, product);
        }
        // Se entrambi sono dello stesso tipo, mantieni il più recente
        else if (isCurrentOffline === isExistingOffline) {
          const currentTime = new Date(product.createdAt || '').getTime();
          const existingTime = new Date(existing.createdAt || '').getTime();
          
          if (currentTime > existingTime) {
            seen.set(key, product);
          }
        }
      }
    }
    
    return Array.from(seen.values());
  }

  async syncPendingOperations(): Promise<void> {
    if (!networkService.isOnline()) {
      OfflineLogger.info('ProductSyncService.syncPendingOperations', 'Offline mode, skipping pending operations sync');
      return;
    }

    if (this.issyncing) {
      OfflineLogger.info('ProductSyncService.syncPendingOperations', 'Sync already in progress, skipping');
      return;
    }

    try {
      this.issyncing = true;
      
      let pendingOperations = await productRepository.getPendingOperations();
      
      if (pendingOperations.length === 0) {
        OfflineLogger.info('ProductSyncService.syncPendingOperations', 'No pending operations to sync');
        return;
      }

      // ✅ NUOVO: Deduplicazione locale dei pending operations
      // Questo previene errori di violazione del vincolo unico quando ci sono duplicati offline
      pendingOperations = this.deduplicatePendingOperations(pendingOperations);
      OfflineLogger.info('ProductSyncService.syncPendingOperations', `After deduplication: ${pendingOperations.length} operations to sync`);

      // Get current user ID for sync
      const { supabase } = await import('@/integrations/supabase/client');
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id;
      
      if (!userId) {
        OfflineLogger.error('ProductSyncService.syncPendingOperations', 'No user ID found');
        return;
      }

      // ✅ MIGLIORATO: Gestione più intelligente delle operazioni pending
      const syncedIds: string[] = [];
      const operationResults = {
        created: 0,
        updated: 0,
        deleted: 0,
        skipped: 0
      };
      
      // ✅ NUOVO: Ottieni prodotti server una volta sola per confronti
      let serverProducts: UserProduct[] = [];
      try {
        serverProducts = await getUserProducts();
      } catch (error) {
        OfflineLogger.error('ProductSyncService.syncPendingOperations', 'Failed to fetch server products for comparison');
        return;
      }
      
      const serverProductsMap = new Map(serverProducts.map(p => [p.id, p]));
      
      // Process each operation with improved duplicate checking
      for (const operation of pendingOperations) {
        try {
          const operationId = `${operation.id}-${operation._syncStatus}`;
          
          if (operation._syncStatus === 'pending') {
            if (this.isOfflineCreatedProduct(operation.id)) {
              // ✅ MIGLIORATO: Check duplicati più intelligente per creazioni
              const isDuplicate = this.checkForServerDuplicate(operation, serverProducts);
              
              if (!isDuplicate) {
                const { addUserProduct } = await import('@/services/products.service');
                await addUserProduct({
                  userId: operation.user_id,
                  name: operation.name,
                  defaultQuantity: operation.default_quantity,
                  defaultUnit: operation.default_unit,
                  photo: operation.photo,
                  barcode: operation.barcode,
                  openfoodfactsId: operation.openfoodfacts_id,
                  categoryId: operation.category_id,
                  attributes: operation.attributes || []
                });
                operationResults.created++;
                OfflineLogger.info('ProductSyncService', `Created new product: ${operation.name}`);
              } else {
                operationResults.skipped++;
                OfflineLogger.info('ProductSyncService', `Skipped duplicate product: ${operation.name}`);
              }
            } else if (serverProductsMap.has(operation.id)) {
              // Update existing product
              const { updateUserProduct } = await import('@/services/products.service');
              await updateUserProduct(operation.id, {
                name: operation.name,
                defaultQuantity: operation.default_quantity,
                defaultUnit: operation.default_unit,
                photo: operation.photo,
                barcode: operation.barcode,
                openfoodfacts_id: operation.openfoodfacts_id,
                categoryId: operation.category_id,
                attributes: operation.attributes || []
              });
              operationResults.updated++;
              OfflineLogger.info('ProductSyncService', `Updated product: ${operation.name}`);
            }
            syncedIds.push(operation.id);
          } else if (operation._syncStatus === 'deleted') {
            if (serverProductsMap.has(operation.id)) {
              const { deleteUserProduct } = await import('@/services/products.service');
              await deleteUserProduct(operation.id);
              operationResults.deleted++;
              OfflineLogger.info('ProductSyncService', `Deleted product: ${operation.id}`);
            }
            syncedIds.push(operation.id);
          }
        } catch (error) {
          OfflineLogger.error('ProductSyncService.syncOperation', `Failed to sync operation for ${operation.id}: ${error}`);
        }
      }
      
      // Mark operations as synced
      if (syncedIds.length > 0) {
        await productRepository.markAsSynced(syncedIds);
        await productRepository.removeDeletedItems();
        
        OfflineLogger.info('ProductSyncService.syncPendingOperations', 
          `Sync completed - Created: ${operationResults.created}, Updated: ${operationResults.updated}, Deleted: ${operationResults.deleted}, Skipped: ${operationResults.skipped}`
        );
      }
      
    } catch (error) {
      OfflineLogger.error('ProductSyncService.syncPendingOperations', error);
    } finally {
      this.issyncing = false;
    }
  }

  // ✅ NUOVO: Check più intelligente per duplicati sul server
  private checkForServerDuplicate(operation: any, serverProducts: UserProduct[]): boolean {
    return serverProducts.some(p => {
      // Match esatto per nome, barcode e categoria
      const nameMatch = p.name.toLowerCase().trim() === operation.name.toLowerCase().trim();
      const barcodeMatch = (p.barcode || '') === (operation.barcode || '');
      const categoryMatch = p.categoryId === operation.category_id;
      
      return nameMatch && barcodeMatch && categoryMatch;
    });
  }

  // ✅ NUOVO: Deduplicazione dei pending operations locali
  private deduplicatePendingOperations(operations: any[]): any[] {
    const seen = new Map<string, any>();
    const deduped: any[] = [];

    for (const op of operations) {
      // Chiave unica: category_id + name + barcode (non considera l'ID che potrebbe variare offline)
      const key = `${op.category_id}|${(op.name || '').toLowerCase().trim()}|${op.barcode || ''}`;
      const existing = seen.get(key);

      if (!existing) {
        // Primo prodotto con questa combinazione: salva
        seen.set(key, op);
        deduped.push(op);
        OfflineLogger.info('ProductSyncService.deduplicatePendingOperations', `Added: ${op.name} (${key})`);
      } else {
        // Duplicato trovato: mantieni il più recente per stato e aggiornamento
        const existingTime = new Date(existing._lastModified || existing.createdAt || '').getTime();
        const currentTime = new Date(op._lastModified || op.createdAt || '').getTime();

        if (currentTime > existingTime) {
          // Rimpiazza con versione più recente
          const idx = deduped.findIndex(p => p.id === existing.id);
          if (idx !== -1) {
            deduped[idx] = op;
            seen.set(key, op);
            OfflineLogger.info('ProductSyncService.deduplicatePendingOperations', `Replaced with newer: ${op.name}`);
          }
        } else {
          OfflineLogger.info('ProductSyncService.deduplicatePendingOperations', `Skipped older duplicate: ${op.name}`);
        }
      }
    }

    if (deduped.length < operations.length) {
      OfflineLogger.info('ProductSyncService.deduplicatePendingOperations', `Removed ${operations.length - deduped.length} duplicates`);
    }

    return deduped;
  }

  async performFullSync(): Promise<UserProduct[]> {
    try {
      OfflineLogger.info('ProductSyncService.performFullSync', 'Starting intelligent full product sync');
      
      // ✅ NUOVO: Statistics prima del cleanup
      const statsBefore = await productRepository.getDebugStats();
      OfflineLogger.info('ProductSyncService.performFullSync', `Before sync - Total: ${statsBefore.total}, Duplicates: ${statsBefore.duplicates}, Pending: ${statsBefore.pending}`);
      
      // Cleanup orphaned records first
      await productRepository.cleanupOrphanedRecords();
      
      // Sync pending operations first, then get fresh data
      await this.syncPendingOperations();
      const products = await this.syncFromServer();
      
      // ✅ NUOVO: Statistics dopo il sync
      const statsAfter = await productRepository.getDebugStats();
      OfflineLogger.info('ProductSyncService.performFullSync', `After sync - Total: ${statsAfter.total}, Duplicates: ${statsAfter.duplicates}, Pending: ${statsAfter.pending}`);
      
      OfflineLogger.info('ProductSyncService.performFullSync', 'Intelligent full product sync completed successfully');
      return products;
    } catch (error) {
      OfflineLogger.error('ProductSyncService.performFullSync', error);
      return this.loadLocalProducts();
    }
  }

  // ✅ NUOVO: Metodo per debug e monitoring
  async getSyncStats(): Promise<{
    lastServerSync: number;
    isSyncing: boolean;
    dbStats: any;
  }> {
    return {
      lastServerSync: this.lastServerSyncTimestamp,
      isSyncing: this.issyncing,
      dbStats: await productRepository.getDebugStats()
    };
  }

  private isOfflineCreatedProduct(id: string): boolean {
    return id.startsWith('offline-') || (id.includes('-') && id.length > 30);
  }
}

export const productSyncService = new ProductSyncService();
